import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronRightRounded,
  ChevronLeftRounded,
  DonutLargeRounded,
  EmojiEventsRounded,
} from '@mui/icons-material';
import {
  getStudentsForClass,
  getSyllabusProgress,
  getClassWeeklyPlan,
  getClassReinforcementReport,
  getTeacherCurriculum,
  getCustomSyllabus,
  getClassDashboardMetrics,
  refreshClassMetrics,
  ClassWeeklyPlan,
  ClassReinforcementResponse,
  ReinforcementTopic,
  CustomSyllabusResponse,
} from '../services/api';
import {
  SyllabusItem,
  TeacherCurriculumSubject,
  DashboardResponse,
  ReinforcementStudent,
  Student,
} from '../types';
import { resolveTeacherClassContext, saveTeacherClassContext } from '../utils/teacherClassContext';
import { computeStudentScore } from '../utils/studentScore';
import { useTeacherAuth } from '../context/TeacherAuthContext';
import TeacherLayout from './teacher/TeacherLayout';
import EntryAssessmentCard from './teacher/EntryAssessmentCard';
import DateRangeControl, { DateRange, rangeForDays } from './teacher/DateRangeControl';

interface LocationState {
  school: string;
  grade: number;
  section?: string;
}

type CurriculumSource = 'ncert' | 'global' | 'custom';

interface DashboardCurriculumSubject extends Omit<TeacherCurriculumSubject, 'subject'> {
  /** null for Global/free-text pseudo-subjects (they don't map to an NCERT subject). */
  subject: TeacherCurriculumSubject['subject'] | null;
  source: CurriculumSource;
  coverageKey: string;
}

const CUSTOM_TOPICS_SUBJECT: DashboardCurriculumSubject = {
  subject: null,
  label: 'Your own topics',
  is_substitute: false,
  chapters: [],
  source: 'custom',
  coverageKey: '__custom__',
};

/** Display label + stable bucket key for a Global chapter's subject. Chapters
 *  with no subject fall into one "General" group. */
const globalSubjectLabel = (subject?: string | null): string => (subject ?? '').trim() || 'General';
const globalCoverageKey = (subject?: string | null): string =>
  `__global__:${globalSubjectLabel(subject).toLowerCase()}`;

/** One card PER SUBJECT inside the Global syllabus (parallel to NCERT), in the
 *  order subjects first appear in the chapter list. */
const buildGlobalSubjects = (
  syllabus: CustomSyllabusResponse | null
): DashboardCurriculumSubject[] => {
  if (!syllabus || syllabus.chapters.length === 0) return [];
  const order: string[] = [];
  const byLabel = new Map<string, DashboardCurriculumSubject>();
  syllabus.chapters.forEach((c) => {
    const label = globalSubjectLabel(c.subject);
    if (!byLabel.has(label)) {
      order.push(label);
      byLabel.set(label, {
        subject: null,
        label,
        is_substitute: false,
        source: 'global',
        coverageKey: globalCoverageKey(c.subject),
        chapters: [],
      });
    }
    byLabel.get(label)!.chapters.push({
      title: c.title,
      chapter_number: null as unknown as number,
      sub_topics: c.sub_topics,
    });
  });
  return order.map((l) => byLabel.get(l)!);
};

const buildNcertSubjects = (subjects: TeacherCurriculumSubject[]): DashboardCurriculumSubject[] =>
  subjects.map((subj) => ({
    ...subj,
    source: 'ncert',
    coverageKey: syllabusSubjectKey(subj.subject),
  }));

const SUBJECT_LABELS: Record<string, string> = {
  maths: 'Maths',
  science: 'Science',
  social: 'Social',
};

/** Stable subject bucket for syllabus progress rows (matches SyllabusChecklist). */
const syllabusSubjectKey = (subject: string | null | undefined): string => subject ?? '';

/** Classify a ticked item into a coverage bucket: free-text, a Global subject
 *  (resolved from the chapter → subject map), or an NCERT subject. */
const classifySyllabusItem = (
  item: SyllabusItem,
  globalChapterSubject: Map<string, string>
): string => {
  if ((item.subject ?? '') === '' && (item.chapter_title ?? '') === '') return '__custom__';
  if ((item.subject ?? '') === '') {
    const subj = globalChapterSubject.get((item.chapter_title ?? '').trim().toLowerCase());
    return globalCoverageKey(subj);
  }
  return syllabusSubjectKey(item.subject);
};

const displaySubTopic = (raw: string): string => raw.replace(/^\d+(?:\.\d+)*\s+/, '');

const formatCoveredBreakdown = (
  coveredBySubject: Map<string, number>,
  subjects: DashboardCurriculumSubject[]
): string => {
  const parts: string[] = [];
  subjects.forEach((subj) => {
    const count = coveredBySubject.get(subj.coverageKey) ?? 0;
    if (count > 0) parts.push(`${subj.label} ${count}`);
  });
  return parts.length > 0 ? parts.join(' · ') : 'None ticked off yet';
};

// Understanding-level categories (same wording as ClassDetails, so a student's
// badge reads the same across the teacher app).
type StudentCategoryKey = ReinforcementStudent['category'];

const STUDENT_CATEGORY_META: Record<
  StudentCategoryKey,
  { label: string; hint: string; dot: string; chip: string; avatar: string; hex: string }
> = {
  bright: {
    label: 'Ahead',
    hint: 'Landing concepts on the first go',
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    avatar: 'bg-emerald-500 text-white',
    hex: '#10b981',
  },
  hardworking: {
    label: 'On track',
    hint: 'Landing concepts after a few tries',
    dot: 'bg-sky-500',
    chip: 'bg-sky-50 text-sky-700 ring-sky-100',
    avatar: 'bg-sky-500 text-white',
    hex: '#0ea5e9',
  },
  disengaged: {
    label: 'Needs support',
    hint: 'Concepts not landing yet',
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-700 ring-amber-100',
    avatar: 'bg-amber-500 text-white',
    hex: '#f59e0b',
  },
  unknown: {
    label: 'No data yet',
    hint: 'Too few chats to place them',
    dot: 'bg-slate-300',
    chip: 'bg-slate-50 text-slate-600 ring-slate-200',
    avatar: 'bg-slate-300 text-slate-700',
    hex: '#cbd5e1',
  },
};

// Fixed display order — brightest first, "unknown" last.
const STUDENT_CATEGORY_ORDER: StudentCategoryKey[] = [
  'bright',
  'hardworking',
  'disengaged',
  'unknown',
];


// Circular-avatar initials, e.g. "Aarav Sharma" → "AS".
const avatarInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

// Donut annulus segment path (outer arc, in to inner, inner arc back, close).
const annulusPath = (
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  start: number,
  end: number,
) => {
  const large = end - start > Math.PI ? 1 : 0;
  const x1o = cx + outerR * Math.cos(start);
  const y1o = cy + outerR * Math.sin(start);
  const x2o = cx + outerR * Math.cos(end);
  const y2o = cy + outerR * Math.sin(end);
  const x1i = cx + innerR * Math.cos(end);
  const y1i = cy + innerR * Math.sin(end);
  const x2i = cx + innerR * Math.cos(start);
  const y2i = cy + innerR * Math.sin(start);
  return [
    `M ${x1o} ${y1o}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${x2o} ${y2o}`,
    `L ${x1i} ${y1i}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${x2i} ${y2i}`,
    'Z',
  ].join(' ');
};

interface BreakdownStudent {
  id: number;
  name: string;
}

interface BreakdownSegment {
  key: string;
  label: string;
  hex: string;
  /** Tailwind bg + text classes for the student avatar. */
  avatarClass: string;
  students: BreakdownStudent[];
}

/**
 * Interactive donut breakdown: a donut on the left and, on the right, either the
 * segment legend or — once a slice/legend item is picked — a scrollable list of
 * that segment's students. Used for both "understanding level" and "participation".
 */
const StudentBreakdownDonut: React.FC<{
  segments: BreakdownSegment[];
  emptyMessage?: string;
}> = ({ segments, emptyMessage = 'No data yet.' }) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);

  const total = segments.reduce((sum, s) => sum + s.students.length, 0);

  if (total === 0) {
    return (
      <div className="flex h-[220px] items-center justify-center rounded-xl bg-slate-50 px-4 text-center text-sm text-slate-500">
        {emptyMessage}
      </div>
    );
  }

  const size = 220;
  const cx = size / 2;
  const cy = size / 2;
  const innerR = 58;
  const outerR = 82;

  // Arcs in segment order; empty segments are dropped.
  let cursor = -Math.PI / 2;
  const arcs = segments
    .map((seg) => {
      const count = seg.students.length;
      const angle = (count / total) * Math.PI * 2;
      const start = cursor;
      const end = cursor + angle;
      cursor = end;
      return { seg, count, start, end, mid: (start + end) / 2 };
    })
    .filter((a) => a.count > 0);

  // A single non-zero segment can't be drawn as an arc (start === end) — draw a
  // full ring instead.
  const isFullRing = arcs.length === 1;

  const selectedSeg = selected ? segments.find((s) => s.key === selected) ?? null : null;

  return (
    <div className="flex flex-col gap-6 sm:min-h-[240px] sm:flex-row sm:items-center">
      <style>{`@keyframes uslFade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}`}</style>

      {/* Donut */}
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="h-[210px] w-[210px] flex-none"
        role="img"
        aria-label="Student breakdown"
      >
        {isFullRing ? (
          <circle
            cx={cx}
            cy={cy}
            r={(innerR + outerR) / 2}
            fill="none"
            stroke={arcs[0].seg.hex}
            strokeWidth={outerR - innerR}
            className="cursor-pointer"
            onClick={() => setSelected((cur) => (cur === arcs[0].seg.key ? null : arcs[0].seg.key))}
          >
            <title>{`${arcs[0].seg.label}: ${arcs[0].count}`}</title>
          </circle>
        ) : (
          arcs.map((arc) => {
            const active = selected === arc.seg.key;
            const hot = hovered === arc.seg.key;
            const offset = active ? 7 : hot ? 4 : 0;
            const dx = Math.cos(arc.mid) * offset;
            const dy = Math.sin(arc.mid) * offset;
            return (
              <path
                key={arc.seg.key}
                d={annulusPath(cx, cy, innerR, outerR, arc.start, arc.end)}
                fill={arc.seg.hex}
                fillOpacity={selected && !active ? 0.4 : 1}
                transform={`translate(${dx} ${dy})`}
                style={{ transition: 'transform 250ms ease, fill-opacity 250ms ease' }}
                className="cursor-pointer"
                onMouseEnter={() => setHovered(arc.seg.key)}
                onMouseLeave={() => setHovered((cur) => (cur === arc.seg.key ? null : cur))}
                onClick={() => setSelected((cur) => (cur === arc.seg.key ? null : arc.seg.key))}
              >
                <title>{`${arc.seg.label}: ${arc.count}`}</title>
              </path>
            );
          })
        )}
        <text x={cx} y={cy - 4} textAnchor="middle" fill="#111827" fontSize="30" fontWeight="700">
          {total}
        </text>
        <text x={cx} y={cy + 18} textAnchor="middle" fill="#6B7280" fontSize="12">
          students
        </text>
      </svg>

      {/* Right panel: legend (default) or the selected segment's students */}
      <div className="min-w-0 flex-1">
        {selectedSeg ? (
          <div key={selectedSeg.key} style={{ animation: 'uslFade 250ms ease' }}>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="mb-3 inline-flex items-center gap-0.5 text-xs font-semibold text-gray-500 transition hover:text-gray-800"
            >
              <ChevronLeftRounded sx={{ fontSize: 16 }} /> All categories
            </button>
            <div className="mb-1 flex items-center gap-2">
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: selectedSeg.hex }} />
              <span className="text-sm font-bold text-gray-900">{selectedSeg.label}</span>
            </div>
            <p className="mb-3 text-xs font-medium text-gray-500">
              {selectedSeg.students.length} student{selectedSeg.students.length === 1 ? '' : 's'}
            </p>
            {selectedSeg.students.length === 0 ? (
              <p className="px-2 py-3 text-sm text-gray-400">No students in this category yet.</p>
            ) : (
              <ul
                className="flex max-h-[264px] flex-col gap-0.5 overflow-y-auto pr-1"
                style={{ scrollBehavior: 'smooth' }}
              >
                {selectedSeg.students.map((s) => (
                  <li key={s.id}>
                    <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition hover:bg-gray-50">
                      <span
                        className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-[11px] font-bold ${selectedSeg.avatarClass}`}
                      >
                        {avatarInitials(s.name)}
                      </span>
                      <span className="truncate text-sm text-gray-900">{s.name}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <ul key="legend" className="flex flex-col gap-1" style={{ animation: 'uslFade 250ms ease' }}>
            {segments.map((seg) => {
              const count = seg.students.length;
              return (
                <li key={seg.key}>
                  <button
                    type="button"
                    disabled={count === 0}
                    onMouseEnter={() => setHovered(seg.key)}
                    onMouseLeave={() => setHovered((cur) => (cur === seg.key ? null : cur))}
                    onClick={() => setSelected(seg.key)}
                    className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition hover:bg-gray-50 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    <span className="h-3 w-3 flex-none rounded-sm" style={{ backgroundColor: seg.hex }} />
                    <span className="flex-1 truncate text-sm text-gray-700">{seg.label}</span>
                    <span className="text-sm font-semibold text-gray-500">({count})</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------

const TeacherDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, teacher } = useTeacherAuth();
  const state = location.state as LocationState | undefined;
  const classContext = resolveTeacherClassContext(state);

  const school = classContext.school;
  const grade =
    typeof classContext.grade === 'number' ? classContext.grade : Number(classContext.grade);
  const section = classContext.section ?? null;

  const hasClassContext = Boolean(school && grade && !Number.isNaN(grade));

  const navState = { school, grade, section: section ?? undefined };
  const goto = (path: string) => navigate(path, { state: navState });

  // Open a student's full report from the leaderboard (same route/state shape
  // ClassDetails uses), so the score there matches the number shown here.
  const openStudentReport = (id: number) => {
    const student = studentsById.get(id);
    if (!student) return;
    navigate('/student-report', { state: { student, ...navState } });
  };

  const [studentCount, setStudentCount] = useState<number | null>(null);
  // Full enrolled roster (id + name) — the source of truth for who's in the
  // class, so both breakdown donuts cover every student, not just the ones who
  // happen to have activity or a report entry.
  const [roster, setRoster] = useState<BreakdownStudent[]>([]);
  // Full student objects by id, so the leaderboard can open a student's report.
  const [studentsById, setStudentsById] = useState<Map<number, Student>>(new Map());
  const [syllabusItems, setSyllabusItems] = useState<SyllabusItem[]>([]);
  const [curriculum, setCurriculum] = useState<TeacherCurriculumSubject[] | null>(null);
  const [customSyllabus, setCustomSyllabus] = useState<CustomSyllabusResponse | null>(null);
  const [weeklyPlan, setWeeklyPlan] = useState<ClassWeeklyPlan | null>(null);
  const [report, setReport] = useState<ClassReinforcementResponse | null>(null);
  const [dashboardMetrics, setDashboardMetrics] = useState<DashboardResponse | null>(null);
  // Analytics window — drives the activity charts + participation. Reports
  // (knowledge state) are unaffected. Default: last 90 days.
  const [analyticsRange, setAnalyticsRange] = useState<DateRange>(() => rangeForDays(90));

  useEffect(() => {
    if (!hasClassContext || !school || !grade) return;
    saveTeacherClassContext({ school, grade, section });
  }, [hasClassContext, school, grade, section]);

  useEffect(() => {
    if (!hasClassContext || !school || !grade) return;
    let mounted = true;
    getStudentsForClass(school, grade, section)
      .then((rows) => {
        if (!mounted) return;
        setStudentCount(rows.length);
        setRoster(
          rows.map((r) => ({
            id: r.student.id,
            name: r.student.first_name?.trim() || `Student ${r.student.id}`,
          })),
        );
        setStudentsById(new Map(rows.map((r) => [r.student.id, r.student])));
      })
      .catch(() => {
        if (!mounted) return;
        setStudentCount(null);
        setRoster([]);
        setStudentsById(new Map());
      });
    return () => {
      mounted = false;
    };
  }, [hasClassContext, school, grade, section]);

  useEffect(() => {
    if (!token || !hasClassContext || !grade) return;
    let mounted = true;
    getSyllabusProgress(token, grade, section)
      .then((r) => mounted && setSyllabusItems(r.items || []))
      .catch(() => mounted && setSyllabusItems([]));
    getTeacherCurriculum(token, grade)
      .then((r) => mounted && setCurriculum(r.subjects || []))
      .catch(() => mounted && setCurriculum([]));
    getCustomSyllabus(token, grade, section)
      .then((r) => mounted && setCustomSyllabus(r))
      .catch(() => mounted && setCustomSyllabus(null));
    return () => {
      mounted = false;
    };
  }, [token, hasClassContext, grade, section]);

  useEffect(() => {
    if (!token || !hasClassContext || !grade) return;
    let mounted = true;
    getClassWeeklyPlan(token, grade, section ?? undefined)
      .then((p) => mounted && setWeeklyPlan(p))
      .catch(() => mounted && setWeeklyPlan(null));
    return () => {
      mounted = false;
    };
  }, [token, hasClassContext, grade, section]);

  useEffect(() => {
    if (!token || !hasClassContext || !grade) return;
    let mounted = true;
    getClassReinforcementReport(token, grade, section, false, undefined, undefined, school)
      .then((r) => mounted && setReport(r))
      .catch(() => mounted && setReport(null));
    return () => {
      mounted = false;
    };
  }, [token, hasClassContext, school, grade, section]);

  useEffect(() => {
    if (!hasClassContext || !school || !grade) return;
    let mounted = true;
    // Refresh the metrics cache for this window before reading it, so the
    // participation donut reflects real activity rather than a stale (or never
    // populated) cache. The dashboard read path never recomputes on its own, so
    // without this a class whose metrics haven't been refreshed shows students
    // who did use the coach as "never used". Mirrors ClassAnalytics; a failed
    // refresh is non-fatal and falls back to whatever was last computed.
    (async () => {
      try {
        await refreshClassMetrics(
          school,
          grade,
          section ?? undefined,
          true,
          analyticsRange.start,
          analyticsRange.end
        );
      } catch {
        // Fall through and read the last-computed metrics.
      }
      if (!mounted) return;
      try {
        const r = await getClassDashboardMetrics(
          school,
          grade,
          section,
          analyticsRange.start,
          analyticsRange.end
        );
        if (mounted) setDashboardMetrics(r);
      } catch {
        if (mounted) setDashboardMetrics(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [hasClassContext, school, grade, section, analyticsRange.start, analyticsRange.end]);

  const subjectsLabel = useMemo(() => {
    const subs = teacher?.subjects ?? [];
    return subs.map((s) => SUBJECT_LABELS[s] || s).join(', ');
  }, [teacher]);

  const topStruggleTopic = useMemo(() => {
    const topics = report?.report_data?.topics ?? [];
    const withStrugglers = topics.filter((t) => t.struggled > 0);
    return withStrugglers[0] ?? null;
  }, [report]);

  // Topics to plan around — most-struggled first. Drives the "Plan your teaching"
  // block (TVG: the dashboard's #1 job is helping the teacher decide what to cover).
  const attentionTopics = useMemo<ReinforcementTopic[]>(() => {
    const topics = report?.report_data?.topics ?? [];
    return [...topics]
      .filter((t) => t.struggled > 0)
      .sort((a, b) => b.struggled - a.struggled)
      .slice(0, 5);
  }, [report]);

  const curriculumStats = useMemo(() => {
    const ncertSubjects = buildNcertSubjects(curriculum ?? []);
    const globalSubjects = buildGlobalSubjects(customSyllabus);
    // chapter title (lowercased) → its Global subject, so ticked Global rows
    // (which only carry a chapter title) land in the right subject bucket.
    const globalChapterSubject = new Map<string, string>();
    (customSyllabus?.chapters ?? []).forEach((c) => {
      const t = c.title.trim().toLowerCase();
      if (t) globalChapterSubject.set(t, globalSubjectLabel(c.subject));
    });
    const classify = (it: SyllabusItem) => classifySyllabusItem(it, globalChapterSubject);
    const customCount = syllabusItems.filter((it) => classify(it) === '__custom__').length;
    const customSubjects =
      customCount > 0 ? [{ ...CUSTOM_TOPICS_SUBJECT }] : ([] as DashboardCurriculumSubject[]);
    const subjects = [...ncertSubjects, ...globalSubjects, ...customSubjects];
    const totalChapters = subjects.reduce((acc, s) => acc + (s.chapters?.length ?? 0), 0);
    const totalSubTopics = subjects.reduce(
      (acc, s) => acc + s.chapters.reduce((cAcc, ch) => cAcc + (ch.sub_topics?.length ?? 0), 0),
      0
    );
    const coveredBySubject = new Map<string, number>();
    const coveredItemsBySubject = new Map<string, SyllabusItem[]>();
    syllabusItems.forEach((it) => {
      const key = classify(it);
      coveredBySubject.set(key, (coveredBySubject.get(key) ?? 0) + 1);
      const list = coveredItemsBySubject.get(key) ?? [];
      list.push(it);
      coveredItemsBySubject.set(key, list);
    });
    const topicsCovered = syllabusItems.length;
    const coveredBreakdown = formatCoveredBreakdown(coveredBySubject, subjects);
    const ncertChapters = ncertSubjects.reduce((acc, s) => acc + s.chapters.length, 0);
    const globalChapters = globalSubjects.reduce((acc, s) => acc + s.chapters.length, 0);
    const scopeParts: string[] = [];
    if (ncertSubjects.length > 0) {
      scopeParts.push(`NCERT: ${ncertSubjects.length} subject${ncertSubjects.length === 1 ? '' : 's'}`);
    }
    if (globalSubjects.length > 0) {
      scopeParts.push(`Global: ${globalChapters} chapter${globalChapters === 1 ? '' : 's'}`);
    }
    if (customCount > 0) {
      scopeParts.push(`${customCount} own topic${customCount === 1 ? '' : 's'}`);
    }
    const scopeLabel =
      scopeParts.length > 0
        ? `${scopeParts.join(' · ')} · ${totalSubTopics + customCount} topics`
        : 'No curriculum set up yet';
    return {
      subjects,
      ncertSubjects,
      globalSubjects,
      customSubjects,
      totalChapters,
      totalSubTopics: totalSubTopics + customCount,
      coveredBySubject,
      coveredItemsBySubject,
      topicsCovered,
      coveredBreakdown,
      scopeLabel,
      hasAnyCurriculum: subjects.length > 0,
      ncertChapters,
      globalChapters,
    };
  }, [curriculum, customSyllabus, syllabusItems]);

  const planStats = useMemo(() => {
    const homeworks = (weeklyPlan?.homeworks ?? []).filter((h) => h.task);
    const exams = weeklyPlan?.exams ?? [];
    const plannedTopics = weeklyPlan?.topics?.length ?? 0;
    return { homeworks: homeworks.length, exams: exams.length, plannedTopics };
  }, [weeklyPlan]);

  // Participation buckets, straight from the per-student activity snapshots so
  // each bucket lists exactly the students it counts (never / low / active by
  // number of user messages in the window).
  const byName = (a: BreakdownStudent, b: BreakdownStudent) => a.name.localeCompare(b.name);

  // Participation buckets. We start from the full enrolled roster and join each
  // student to their activity snapshot by id — so a student with no activity
  // (no snapshot) correctly lands in "Never used", instead of being dropped.
  const participationSegments = useMemo<BreakdownSegment[]>(() => {
    const snapshots = dashboardMetrics?.student_snapshots ?? [];
    const msgsById = new Map<number, number>();
    const nameById = new Map<number, string>();
    snapshots.forEach((s) => {
      msgsById.set(s.student_id, s.total_user_messages ?? 0);
      if (s.student_name?.trim()) nameById.set(s.student_id, s.student_name.trim());
    });

    // Prefer the enrolled roster; fall back to snapshot students if the roster
    // hasn't loaded yet (or the class endpoint failed).
    const people: BreakdownStudent[] =
      roster.length > 0
        ? roster
        : snapshots.map((s) => ({
            id: s.student_id,
            name: s.student_name?.trim() || `Student ${s.student_id}`,
          }));

    const never: BreakdownStudent[] = [];
    const low: BreakdownStudent[] = [];
    const active: BreakdownStudent[] = [];
    people.forEach((p) => {
      const student: BreakdownStudent = { id: p.id, name: nameById.get(p.id) || p.name };
      const msgs = msgsById.get(p.id) ?? 0;
      if (msgs === 0) never.push(student);
      else if (msgs < 8) low.push(student);
      else active.push(student);
    });
    return [
      { key: 'never', label: 'Never used', hex: '#ef4444', avatarClass: 'bg-red-500 text-white', students: never.sort(byName) },
      { key: 'low', label: 'Low activity', hex: '#f97316', avatarClass: 'bg-orange-500 text-white', students: low.sort(byName) },
      { key: 'active', label: 'Active', hex: '#14b8a6', avatarClass: 'bg-teal-500 text-white', students: active.sort(byName) },
    ];
  }, [dashboardMetrics, roster]);

  // Understanding-level buckets. Same idea: start from the roster and join to the
  // reinforcement report by id; a student with no report entry lands in "Not
  // enough signal yet". Older cached reports may lack the `students` array.
  const understandingSegments = useMemo<BreakdownSegment[]>(() => {
    const reportStudents = report?.report_data?.students ?? [];
    const catById = new Map<number, StudentCategoryKey>();
    const nameById = new Map<number, string>();
    reportStudents.forEach((s) => {
      const key: StudentCategoryKey =
        s.category === 'bright' || s.category === 'hardworking' || s.category === 'disengaged'
          ? s.category
          : 'unknown';
      catById.set(s.student_id, key);
      if (s.name?.trim()) nameById.set(s.student_id, s.name.trim());
    });

    const people: BreakdownStudent[] =
      roster.length > 0 ? roster : reportStudents.map((s) => ({ id: s.student_id, name: s.name }));

    const groups: Record<StudentCategoryKey, BreakdownStudent[]> = {
      bright: [],
      hardworking: [],
      disengaged: [],
      unknown: [],
    };
    people.forEach((p) => {
      const key = catById.get(p.id) ?? 'unknown';
      groups[key].push({ id: p.id, name: nameById.get(p.id) || p.name });
    });
    return STUDENT_CATEGORY_ORDER.map((key) => ({
      key,
      label: STUDENT_CATEGORY_META[key].label,
      hex: STUDENT_CATEGORY_META[key].hex,
      avatarClass: STUDENT_CATEGORY_META[key].avatar,
      students: groups[key].sort(byName),
    }));
  }, [report, roster]);

  // Top 5 students by the combined evaluation score (understanding + quiz +
  // curiosity — the SAME blend the per-student report shows). Prefers the
  // roster name for freshness; only students with enough signal to score appear.
  const topStudents = useMemo<{ id: number; name: string; score: number }[]>(() => {
    const reportStudents = report?.report_data?.students ?? [];
    const nameById = new Map<number, string>();
    roster.forEach((p) => nameById.set(p.id, p.name));
    return reportStudents
      .map((s) => {
        const { score } = computeStudentScore({
          topics: s.topics ?? [],
          quizAvg: s.quiz_avg ?? null,
          curiosityAvg: s.curiosity_avg ?? null,
        });
        return { id: s.student_id, name: nameById.get(s.student_id) || s.name, score };
      })
      .filter((s): s is { id: number; name: string; score: number } => s.score != null)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);
  }, [report, roster]);

  const classTitle = [
    school,
    grade != null && !Number.isNaN(grade) ? `Grade ${grade}` : null,
    section ? `Section ${section}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  if (!hasClassContext) {
    return (
      <TeacherLayout active="home">
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-600">
          Please return to the Classes screen to pick a class before viewing the dashboard.
          <div className="mt-4">
            <button
              type="button"
              onClick={() => navigate('/teacher-view')}
              className="rounded-xl bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
            >
              Pick a class
            </button>
          </div>
        </div>
      </TeacherLayout>
    );
  }

  return (
    <TeacherLayout active="home">
      {/* Header — mirrors the reference "course dashboard" banner */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Class dashboard
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 md:text-[1.65rem]">
            {classTitle}
            {subjectsLabel ? (
              <span className="font-semibold text-violet-700"> — {subjectsLabel}</span>
            ) : null}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Overview of your class, curriculum, and curiosity activity
          </p>
        </div>
        <button
          type="button"
          onClick={() => goto('/class-summary')}
          className="inline-flex items-center gap-1 rounded-xl border border-[#E9E6F4] bg-white px-3 py-2 text-sm font-semibold text-violet-700 shadow-sm hover:bg-violet-50"
        >
          Full overview <ChevronRightRounded sx={{ fontSize: 18 }} />
        </button>
      </div>

      {/* Plan-first: the single thing worth covering next (TVG) */}
      {topStruggleTopic ? (
        <SuggestedNext topic={topStruggleTopic} onPlan={() => goto('/class-weekly-plan')} />
      ) : null}

      {/* 1 — Plan your teaching */}
      <SectionLabel n="1" title="Plan your teaching" hint="where to focus next" />
      <TopicsNeedingAttention topics={attentionTopics} onPlan={() => goto('/class-weekly-plan')} />

      {/* 2 — How your class is doing (+ analytics window) */}
      <div className="mb-3 mt-8 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-violet-100 text-[13px] font-bold text-violet-700">
            2
          </span>
          <h2 className="text-base font-bold text-slate-900">How your class is doing</h2>
        </div>
        <DateRangeControl value={analyticsRange} onChange={setAnalyticsRange} />
      </div>
      <section className="mb-2 rounded-2xl border border-[#E9E6F4] bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <DonutLargeRounded fontSize="small" className="text-slate-400" />
          <div>
            <h2 className="text-base font-bold text-slate-900">Students by understanding level</h2>
            <p className="text-xs text-slate-500">
              Where each student is tracking, from the class reinforcement report
            </p>
          </div>
        </div>
        <StudentBreakdownDonut
          segments={understandingSegments}
          emptyMessage="No student signals yet — levels appear here once students chat with the curiosity."
        />
      </section>

      {/* Top 5 students by combined evaluation score */}
      <section className="mb-2 rounded-2xl border border-[#E9E6F4] bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <EmojiEventsRounded fontSize="small" className="text-amber-400" />
          <div>
            <h2 className="text-base font-bold text-slate-900">Top 5 students</h2>
            <p className="text-xs text-slate-500">
              By overall score — understanding, quiz results and curiosity combined
            </p>
          </div>
        </div>
        <TopStudentsList students={topStudents} onSelect={openStudentReport} />
      </section>

      {token && hasClassContext && school && (
        <EntryAssessmentCard token={token} school={school} grade={grade} section={section} />
      )}

      {/* 3 — Curiosity Coach: who it's helping */}
      <SectionLabel n="3" title="Curiosity Coach — who it's helping" hint="so you know it's working" />
      <section className="mb-6 rounded-2xl border border-[#E9E6F4] bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <DonutLargeRounded fontSize="small" className="text-slate-400" />
          <div>
            <h2 className="text-base font-bold text-slate-900">Usage of coach</h2>
            <p className="text-xs text-slate-500">Who is using the curiosity</p>
          </div>
        </div>
        <StudentBreakdownDonut
          segments={participationSegments}
          emptyMessage="No student activity data yet."
        />
      </section>

      {/* Your teaching so far — the restructured "284" (TVG) */}
      <SectionLabel title="Your teaching so far" hint={subjectsLabel || undefined} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <TeachTile value={String(curriculumStats.topicsCovered)} label="Topics covered" />
        <TeachTile value={String(planStats.homeworks)} label="Homeworks given" />
        <TeachTile value={String(planStats.exams)} label="Exams set" />
      </div>

    </TeacherLayout>
  );
};

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------

const TeachTile: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div className="rounded-2xl border border-[#E9E6F4] bg-white px-4 py-4 shadow-sm">
    <p className="text-2xl font-bold tracking-tight text-slate-900">{value}</p>
    <p className="mt-0.5 text-xs font-medium text-slate-500">{label}</p>
  </div>
);

// Medal tint for the first three ranks; plain slate afterwards.
const RANK_STYLE = [
  'bg-amber-100 text-amber-700',
  'bg-slate-200 text-slate-600',
  'bg-orange-100 text-orange-700',
];

const TopStudentsList: React.FC<{
  students: { id: number; name: string; score: number }[];
  onSelect?: (id: number) => void;
}> = ({ students, onSelect }) => {
  if (students.length === 0) {
    return (
      <div className="flex h-[120px] items-center justify-center rounded-xl bg-slate-50 px-4 text-center text-sm text-slate-500">
        No scores yet — a student appears here once they've chatted enough to be scored.
      </div>
    );
  }
  return (
    <ol className="flex flex-col gap-2">
      {students.map((s, i) => (
        <li key={s.id}>
          <button
            type="button"
            onClick={() => onSelect?.(s.id)}
            className="flex w-full items-center gap-3 rounded-xl border border-[#F1EFFA] px-3 py-2.5 text-left transition hover:border-violet-200 hover:bg-violet-50/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300"
          >
            <span
              className={`flex h-7 w-7 flex-none items-center justify-center rounded-full text-[13px] font-bold ${
                RANK_STYLE[i] ?? 'bg-slate-100 text-slate-500'
              }`}
            >
              {i + 1}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{s.name}</span>
            <span className="flex-none rounded-full bg-violet-50 px-2.5 py-1 text-sm font-bold text-violet-700">
              {s.score}
            </span>
            <ChevronRightRounded className="flex-none text-slate-300" fontSize="small" />
          </button>
        </li>
      ))}
    </ol>
  );
};

const SectionLabel: React.FC<{ n?: string; title: string; hint?: string }> = ({ n, title, hint }) => (
  <div className="mb-3 mt-8 flex flex-wrap items-center gap-2.5">
    {n ? (
      <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-violet-100 text-[13px] font-bold text-violet-700">
        {n}
      </span>
    ) : null}
    <h2 className="text-base font-bold text-slate-900">{title}</h2>
    {hint ? <span className="text-xs text-slate-400">{hint}</span> : null}
  </div>
);

/** Plan-first banner: the one topic worth revisiting next (TVG's #1 priority). */
const SuggestedNext: React.FC<{ topic: ReinforcementTopic; onPlan: () => void }> = ({
  topic,
  onPlan,
}) => {
  const subj = topic.subject ? SUBJECT_LABELS[topic.subject] || topic.subject : null;
  return (
    <div className="relative mb-6 flex flex-wrap items-center gap-4 overflow-hidden rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 p-5 text-white shadow-md">
      <span className="text-3xl leading-none">🎯</span>
      <div className="min-w-0 flex-1">
        <p className="text-[0.66rem] font-bold uppercase tracking-[0.14em] text-white/80">
          Suggested to cover next
        </p>
        <h3 className="mt-1 text-xl font-bold tracking-tight">Revisit {topic.topic}</h3>
        <p className="mt-0.5 text-sm text-white/90">
          {topic.struggled} of {topic.n_students} student{topic.n_students === 1 ? '' : 's'} still
          shaky here{subj ? ` in ${subj}` : ''}.
        </p>
      </div>
      <button
        type="button"
        onClick={onPlan}
        className="rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-violet-700 hover:bg-violet-50"
      >
        Plan this session →
      </button>
    </div>
  );
};

/** "Plan your teaching" list — most-struggled topics, each with a % and an action. */
const TopicsNeedingAttention: React.FC<{ topics: ReinforcementTopic[]; onPlan: () => void }> = ({
  topics,
  onPlan,
}) => {
  if (topics.length === 0) {
    return (
      <div className="rounded-2xl border border-[#E9E6F4] bg-white p-5 text-sm text-slate-500 shadow-sm">
        No struggle signals yet — topics needing attention appear here once students chat about them.
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2.5 rounded-2xl border border-[#E9E6F4] bg-white p-5 shadow-sm">
      {topics.map((t) => {
        const pct = t.n_students > 0 ? Math.round((t.struggled / t.n_students) * 100) : 0;
        const isWatch = !t.flagged && pct >= 33;
        const barColor = t.flagged ? '#fb7185' : isWatch ? '#f59e0b' : '#10b981';
        const chip = t.flagged
          ? { text: 'Revision required', cls: 'bg-rose-100 text-rose-700' }
          : isWatch
            ? { text: 'Watch', cls: 'bg-amber-100 text-amber-700' }
            : { text: 'On track', cls: 'bg-emerald-100 text-emerald-700' };
        const subj = t.subject ? SUBJECT_LABELS[t.subject] || t.subject : null;
        return (
          <div
            key={`${t.subject ?? ''}:${t.topic}`}
            className={`flex items-center gap-4 rounded-xl border p-3.5 ${
              t.flagged ? 'border-rose-100 bg-rose-50/40' : 'border-[#E9E6F4] bg-white'
            }`}
          >
            <div className="w-24 flex-none sm:w-28">
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, backgroundColor: barColor }}
                />
              </div>
              <p className="mt-1.5 text-[11px] font-semibold text-slate-500">{pct}% struggling</p>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-slate-900">{t.topic}</p>
              <p className="text-xs text-slate-500">
                {t.struggled} of {t.n_students} student{t.n_students === 1 ? '' : 's'}
                {subj ? ` · ${subj}` : ''}
              </p>
            </div>
            <span className={`flex-none rounded-full px-2.5 py-1 text-[0.67rem] font-bold ${chip.cls}`}>
              {chip.text}
            </span>
            <button
              type="button"
              onClick={onPlan}
              className="flex-none text-sm font-semibold text-violet-700 hover:underline"
            >
              Plan →
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default TeacherDashboard;
